import { BaseObj } from '@/objects/BaseObj';
import { useBaseObjEntityStateHandler } from '@/objects/entitystatehandler/entitystatehandler';
import { isRef, ref, unref } from 'vue';
import type { EntityState } from './../enums/EntityState';

export class EventLiveStyles extends BaseObj {
    public ID: number;
    public __id: number;

    public CustomCss: string | null;
    public __customcss: string | null;

    public PrimaryColor: string | null;
    public __primarycolor: string | null;

    public SecondaryColor: string | null;
    public __secondarycolor: string | null;

    public ThemeID: number | null;
    public __themeid: number | null;

    public __entityStateHandler;
    public __selfEntityState: EntityState;
    public __entityState: any;
    public get EntityState(): EntityState {
        return unref(this.__entityState);
    }
    /***
     * @deprecated Use the static FromJsValues method instead of the constructor to get type safety and default values
     */
    public constructor(values: {
        ID?: number;
        CustomCss?: string | null;
        EntityState?: EntityState;
        PrimaryColor?: string | null;
        SecondaryColor?: string | null;
        ThemeID?: number | null;
    }) {
        super();
        this.__entityState = ref(values.EntityState ?? 'Unchanged');
        this.__selfEntityState = values.EntityState ?? 'Unchanged';
        this.__entityStateHandler = useBaseObjEntityStateHandler<EventLiveStyles>(unref(this.__entityState), (states) => {
            if (isRef(this.__entityState)) {
                this.__entityState.value = states.EntityStateWithChildrenConsidered;
            } else {
                console.warn('EventLiveStyles tried to set entitystate, but entitystate is not a ref! This should never happen');
            }
            this.__selfEntityState = states.SelfEntityState;
        });
        this.ID = values.ID ?? 0;
        this.__id = values.ID ?? 0;
        Object.defineProperty(this, 'ID', {
            get(): number {
                return this.__id;
            },
            enumerable: true,
            configurable: true,
        });

        this.CustomCss = values.CustomCss ?? null;
        this.__customcss = values.CustomCss ?? null;
        Object.defineProperty(this, 'CustomCss', {
            get(): string | null {
                return this.__customcss;
            },
            set(value: string | null) {
                this.__entityStateHandler.propertyChanged('CustomCss', this.__customcss, value);
                this.__customcss = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.PrimaryColor = values.PrimaryColor ?? null;
        this.__primarycolor = values.PrimaryColor ?? null;
        Object.defineProperty(this, 'PrimaryColor', {
            get(): string | null {
                return this.__primarycolor;
            },
            set(value: string | null) {
                this.__entityStateHandler.propertyChanged('PrimaryColor', this.__primarycolor, value);
                this.__primarycolor = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.SecondaryColor = values.SecondaryColor ?? null;
        this.__secondarycolor = values.SecondaryColor ?? null;
        Object.defineProperty(this, 'SecondaryColor', {
            get(): string | null {
                return this.__secondarycolor;
            },
            set(value: string | null) {
                this.__entityStateHandler.propertyChanged('SecondaryColor', this.__secondarycolor, value);
                this.__secondarycolor = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.ThemeID = values.ThemeID ?? null;
        this.__themeid = values.ThemeID ?? null;
        Object.defineProperty(this, 'ThemeID', {
            get(): number | null {
                return this.__themeid;
            },
            set(value: number | null) {
                this.__entityStateHandler.propertyChanged('ThemeID', this.__themeid, value);
                this.__themeid = value;
            },
            enumerable: true,
            configurable: true,
        });
    }

    public _setNotifyParentOfModificationCallback(callback: () => void) {
        this.__entityStateHandler.setNotifyParentOfModificationCallback(callback);
    }

    public serialize(): string {
        return (
            '{' +
            `"ID": ${JSON.stringify(this.ID)}` +
            ',' +
            `"CustomCss": ${JSON.stringify(this.CustomCss)}` +
            ',' +
            `"EntityState": ${unref(this.__selfEntityState) == 'Unchanged' ? 0 : unref(this.__selfEntityState) == 'Deleted' ? 1 : unref(this.__selfEntityState) == 'Modified' ? 2 : unref(this.__selfEntityState) == 'Added' ? 3 : 0}` +
            ',' +
            `"PrimaryColor": ${JSON.stringify(this.PrimaryColor)}` +
            ',' +
            `"SecondaryColor": ${JSON.stringify(this.SecondaryColor)}` +
            ',' +
            `"ThemeID": ${JSON.stringify(this.ThemeID)}` +
            ',' +
            `"ChangedColumns": ${unref(this.__selfEntityState) !== 'Modified' ? 'null' : JSON.stringify(this.__entityStateHandler.getChangedPropertiesArray())}` +
            '}'
        );
    }

    /**
     * Sets the entity state to Deleted
     */
    public Delete() {
        if (this.__selfEntityState === 'Added') {
            throw new Error('Cannot delete an object that is in Added state. It has not been saved yet.');
        }
        this.__selfEntityState = 'Deleted';
        this.__entityStateHandler.entityStateChanged(this.__selfEntityState);
    }

    /**
     * Undeletes the object by setting resetting the EntityState
     *
     * If the object has modifications, the new state will be 'Modified', otherwise 'Unchanged'
     */
    public DeleteUndo() {
        this.__selfEntityState = 'Unchanged';
        this.__entityStateHandler.entityStateChanged(this.__selfEntityState);
    }

    /**
     * Sets the entity state to Added
     *
     * Also sets the ID to the supplied ID. If no id parameter supplied, the previous id is kept
     *
     * Note: This does not do anything with the children, so use with caution
     *
     * The use case of this is to make copies of existing objects as new objects without having to fill every field.
     */
    public SetEntityStateAsAddedAndResetId(id?: number) {
        if (this.EntityState !== 'Unchanged') {
            throw new Error('Changing a modified object to Added is not supported.');
        }
        this.__selfEntityState = 'Added';
        if (id !== undefined) {
            this.__id = id;
        }
        this.__entityStateHandler.entityStateChanged(this.__selfEntityState);
    }

    /**
     * Creates an instance of EventLiveStyles from the supplied values.
     * Missing values have default values
     * @param values The values you want to set
     * @returns An object instance with the supplied values set
     */
    public static FromJsValues(values: {
        ID?: number;
        CustomCss?: string | null;
        PrimaryColor?: string | null;
        SecondaryColor?: string | null;
        ThemeID?: number | null;
        EntityState?: 'Added' | 'Unchanged';
    }): EventLiveStyles {
        return new EventLiveStyles(values);
    }

    public static _FromServerJson(json: any): EventLiveStyles {
        return new EventLiveStyles({
            ID: json.ID,
            CustomCss: json.CustomCss ?? '',
            PrimaryColor: json.PrimaryColor ?? '',
            SecondaryColor: json.SecondaryColor ?? '',
            ThemeID: json.ThemeID ?? null,
            EntityState: 'Unchanged',
        });
    }

    /**
     * Creates an instance of EventLiveStyles from another instance of EventLiveStyles.
     * @param other The EventLiveStyles you want to clone
     * @returns A cloned EventLiveStyles
     */
    public static FromOther(other: EventLiveStyles): EventLiveStyles {
        if (other.EntityState !== 'Unchanged') {
            throw new Error('Cloning an object with EntityState other than Unchanged is not supported');
        }
        return EventLiveStyles._FromServerJson(JSON.parse(other.serialize()));
    }

    /**
     * Deepclones the object.
     */
    public DeepClone() {
        return EventLiveStyles.FromOther(this);
    }

    /**
     * IsPropertyChanged.
     */
    public IsPropertyChanged(...props: ('ID' | 'CustomCss' | 'PrimaryColor' | 'SecondaryColor' | 'ThemeID')[]) {
        return this.__entityStateHandler.isPropertyChanged(...props);
    }
    static NAME_OF_CLASS = 'EventLiveStyles';
}
